import { Card, CardBody, CardHeader, Text } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { add, endOfMonth } from 'date-fns';
import { groupBy, reduce } from 'lodash';
import { useMemo } from 'react';

import { usePortfolios } from '../../api/portfolio';
import { useWithdrawals } from '../../api/withdrawal';
import { formatDate, SERVER_DATE_FORMAT, today } from '../../utils/dateUtils';
import { moneyCol, textCol } from '../../widgets/Table/columnHelpers/colDef';
import { Table } from '../../widgets/Table/Table';

interface Entry {
    portfolioGuid?: string;
    accountAlias?: string;
    cash: number;
    totalWithdrawals: number;
    difference: number;
}

export const NotEnoughCashForWithdrawals = () => {
    const fromDate = today();
    const toDate = endOfMonth(add(today(), { months: 1 }));

    const { data: withdrawalsPage } = useWithdrawals({
        columnFilters: [
            {
                id: 'cashflowDate',
                value: {
                    from: formatDate(fromDate, SERVER_DATE_FORMAT),
                    to: formatDate(toDate, SERVER_DATE_FORMAT),
                },
            },
        ],
    });

    const { data: portfoliosPage } = usePortfolios({ pagination: { pageIndex: 0, pageSize: 99999999 } });

    const data = useMemo(() => {
        if (!withdrawalsPage || !portfoliosPage) {
            return [];
        }

        const grouped = groupBy(withdrawalsPage.withdrawals, 'pId');

        return reduce(
            grouped,
            (result, withdrawals, pId) => {
                const portfolio = portfoliosPage.portfolios.find((p) => p.base.pId === pId);
                const totalWithdrawals = withdrawals.reduce(
                    (res, w) => (w.cashflowFinalAmount ? res + w.cashflowFinalAmount : res),
                    0,
                );

                if (!portfolio) {
                    return result;
                }

                const difference = portfolio.base.cash - totalWithdrawals;
                return difference < 0
                    ? [
                          ...result,
                          {
                              portfolioGuid: portfolio.base.portfolioGuid,
                              accountAlias: portfolio.base.accountAlias,
                              cash: portfolio.base.cash,
                              totalWithdrawals,
                              difference: portfolio.base.cash - totalWithdrawals,
                          },
                      ]
                    : result;
            },
            [] as Entry[],
        );
    }, [withdrawalsPage, portfoliosPage]);

    const table = useReactTable({
        data,
        columns,
        defaultColumn: {
            enableColumnFilter: false,
        },
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <Card w="100%">
            <CardHeader px={5} py={3} borderBottomWidth={1}>
                <Text size="sm" fontWeight="medium" flexShrink={0}>
                    Upcoming withdrawals without enough cash ({formatDate(fromDate)} - {formatDate(toDate)})
                </Text>
            </CardHeader>
            <CardBody p={3}>
                {data.length > 0 ? (
                    <>
                        <Table table={table} allowXOverflow />
                    </>
                ) : (
                    <Text px={2}>
                        <i>All portfolios have the required amount of cash for upcoming withdrawals</i>
                    </Text>
                )}
            </CardBody>
        </Card>
    );
};

const columnHelper = createColumnHelper<Entry>();
const columns = [
    columnHelper.accessor('portfolioGuid', textCol({ id: 'portfolioGuid' })),
    columnHelper.accessor('accountAlias', textCol({ id: 'accountAlias' })),
    columnHelper.accessor('cash', moneyCol({ id: 'cash' })),
    columnHelper.accessor('totalWithdrawals', moneyCol({ id: 'totalWithdrawals' })),
    columnHelper.accessor('difference', moneyCol({ id: 'difference' })),
];
